import React, { useRef, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import classNames from "classnames";
import { Link } from "gatsby";
import PropTypes from "prop-types";

import RichTextBlock from "./RichTextBlock";
import InvisibleReCAPTCHA from "./InvisibleReCAPTCHA";
import GatsbyImageWithSVGSupport from "./GatsbyImageWithSVGSupport";

import { isValidEmail } from "../utils/utils";
import { useSiteMetadata } from "../hooks/useSiteMetadata";

import "../styles/components/WhitePaperForm.scss";

const SuccessButton = ({ submittedFormLink }) => {
    let buttonProps = {};

    if (submittedFormLink.downloadAsset) {
        buttonProps.as = "a";
        buttonProps.href = submittedFormLink.downloadAsset.url;
        buttonProps.target = "_blank";
    } else if (submittedFormLink.url.startsWith("/")) {
        buttonProps.as = Link;
        buttonProps.to = submittedFormLink.url;
    } else {
        buttonProps.as = "a";
        buttonProps.href = submittedFormLink.url;
    }

    return (
        <Button className="success-button" {...buttonProps}>
            {submittedFormLink.icon && (
                <GatsbyImageWithSVGSupport
                    className="me-3"
                    image={submittedFormLink.icon}
                />
            )}
            {submittedFormLink.label}
        </Button>
    );
};

const WhitePaperForm = ({
    lead,
    successMessage,
    submittedFormLink,
    submitButtonLabel,
}) => {
    const [email, setEmail] = useState("");

    //form state
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);
    const [errors, setErrors] = useState({});
    const [submissionError, setSubmissionError] = useState(null);

    const setValidity = (field, valid) =>
        setErrors({ ...errors, [field]: !valid });

    const updateEmail = e => {
        setEmail(e.target.value);
        setValidity("email", isValidEmail(e.target.value));
    };

    const isValid = () => {
        const _errors = {
            email: !isValidEmail(email),
        };
        setErrors(_errors);
        return !Object.keys(_errors).some(key => _errors[key]);
    };

    const {
        endpoints: { demo: endpointUrl },
    } = useSiteMetadata();

    const postFormData = async reCAPTCHAToken => {
        const formData = {
            email,
            recaptcha_token: reCAPTCHAToken,
            referer: window?.location?.href || "",
            short_form: true,
        };
        const response = await fetch(endpointUrl, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
        });
        return response.json();
    };

    const submitForm = reCAPTCHAToken => {
        setSubmitting(true);
        if (reCAPTCHAToken && isValid()) {
            postFormData(reCAPTCHAToken)
                .then(data => {
                    setSubmissionError(data.error ? data.msg : null);
                    setSubmitted(true);
                })
                .catch(error => console.error(error));
        } else {
            setHasSubmitAttempt(true);
        }
        reCAPTCHARef.current.reset();
        setSubmitting(false);
    };

    const reCAPTCHARef = useRef(null);

    const executeReCAPTCHA = async e => {
        e.preventDefault();
        try {
            const token = await reCAPTCHARef.current.executeAsync();
            submitForm(token);
        } catch (error) {
            console.error(error);
        }
    };

    if (submitted && !submissionError) {
        return (
            <div>
                <RichTextBlock content={successMessage} />
                <SuccessButton submittedFormLink={submittedFormLink} />
            </div>
        );
    }

    return (
        <div>
            <RichTextBlock content={lead} />
            <Form
                className={classNames("white-paper-form", {
                    "has-submit-attempt": hasSubmitAttempt,
                })}
                key="white-paper-form"
                onSubmit={executeReCAPTCHA}
            >
                <Row>
                    <Col className="mb-3" lg={8} xl={9}>
                        <Form.Group controlId="email">
                            <Form.Control
                                type="email"
                                size="lg"
                                required
                                value={email}
                                onChange={updateEmail}
                                placeholder="Email"
                                className={classNames({
                                    invalid: !!errors["email"],
                                })}
                            />
                        </Form.Group>
                    </Col>
                    <Col lg={4} xl={3}>
                        <Button
                            variant="primary"
                            type="submit"
                            size="lg"
                            className="w-100"
                            disabled={submitting}
                        >
                            {submitButtonLabel || "Send"}
                        </Button>
                    </Col>
                </Row>
                {submissionError && (
                    <Alert className="py-2" variant="danger">
                        {submissionError}
                    </Alert>
                )}
                <InvisibleReCAPTCHA innerRef={reCAPTCHARef} />
            </Form>
        </div>
    );
};

WhitePaperForm.propTypes = {
    submitButtonLabel: PropTypes.string,
};

export default WhitePaperForm;
